import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Pencil, TrendingDown, TrendingUp } from "lucide-react";
import { formatCurrency } from "@/lib/dashboard-utils";

export function PLPeriodBlock({ testid, period, revenueLines, cost, result, extraCostLines = [], onCostClick, costTestid, costBreakdown, costExpanded, onCostToggle, onCostBreakdownCollapse }) {
  // result > 0 → profit (green), result == 0 → breakeven, result < 0 → loss (coral)
  const isProfit = result > 0;
  const isBreakeven = Math.abs(result) < 0.005;
  const statusLabel = isBreakeven
    ? "Breakeven"
    : isProfit
      ? "Profit"
      : "Loss";
  const statusColor = isBreakeven
    ? "text-[#5C746A] bg-[#F0F4F2]"
    : isProfit
      ? "text-[#1B5E40] bg-[#E6F2EC]"
      : "text-[#B65A45] bg-[#FFF2EF]";
  const Arrow = isBreakeven ? null : isProfit ? TrendingUp : TrendingDown;
  const fmt = (n) =>
    new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      currencyDisplay: "narrowSymbol",
    }).format(n);
  const totalRevenue = revenueLines.reduce((s, r) => s + (r.amount || 0), 0);
  return (
    <div data-testid={testid} className="p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <div className="text-[10px] sm:text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
          {period}
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] sm:text-xs font-medium ${statusColor}`}
        >
          {Arrow ? <Arrow className="h-3 w-3" strokeWidth={2} /> : null}
          {statusLabel}
        </span>
      </div>
      <div
        data-testid={`${testid}-result`}
        className={`font-mono-data text-2xl sm:text-3xl font-light tracking-tighter mt-3 leading-none ${
          isBreakeven
            ? "text-[#1B3B2E]"
            : isProfit
              ? "text-[#1B5E40]"
              : "text-[#D36C56]"
        }`}
      >
        {isProfit && !isBreakeven ? "+" : ""}
        {fmt(result)}
      </div>
      <div className="mt-4 text-[11px] sm:text-xs text-[#5C746A] space-y-1">
        {revenueLines.map((r) => {
          const pct =
            totalRevenue > 0
              ? +((r.amount / totalRevenue) * 100).toFixed(1)
              : 0;
          return (
            <div key={r.label} className="flex items-baseline gap-3">
              <span className="flex-1 min-w-0 truncate">{r.label}</span>
              <span className="font-mono-data text-[#8A9C93] tabular-nums whitespace-nowrap w-14 text-right pr-2 shrink-0">
                {pct.toFixed(1)}%
              </span>
              <span className="font-mono-data text-[#1B3B2E] tabular-nums whitespace-nowrap w-28 text-right shrink-0">
                {fmt(r.amount)}
              </span>
            </div>
          );
        })}
        <div className="flex items-baseline gap-3 pt-1 mt-1 border-t border-[#F0F4F2]">
          <span className="flex-1 min-w-0 font-semibold text-[#1B3B2E] truncate">
            Total revenue
          </span>
          <span className="font-mono-data text-[#8A9C93] tabular-nums whitespace-nowrap w-14 text-right pr-2 shrink-0">
            100.0%
          </span>
          <span className="font-mono-data font-semibold text-[#1B3B2E] tabular-nums whitespace-nowrap w-28 text-right shrink-0">
            {fmt(totalRevenue)}
          </span>
        </div>
        <div className="flex items-baseline gap-3">
          {onCostToggle ? (
            <button
              type="button"
              onClick={onCostToggle}
              data-testid={`${costTestid || `${testid}-op-cost`}-toggle`}
              aria-expanded={!!costExpanded}
              className="flex-1 min-w-0 flex items-baseline gap-3 rounded-sm hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors cursor-pointer text-left"
            >
              <span className="flex-1 min-w-0 truncate flex items-center gap-1.5">
                Operating cost
                {costExpanded ? (
                  <ChevronDown className="h-3 w-3 text-[#8A9C93] shrink-0" strokeWidth={2} />
                ) : (
                  <ChevronRight className="h-3 w-3 text-[#8A9C93] shrink-0" strokeWidth={2} />
                )}
              </span>
              <span className="w-14 pr-2 shrink-0" aria-hidden="true" />
              <span className="font-mono-data text-[#B65A45] tabular-nums whitespace-nowrap w-28 text-right shrink-0">
                −{fmt(cost)}
              </span>
            </button>
          ) : onCostClick ? (
            <button
              type="button"
              onClick={onCostClick}
              data-testid={costTestid}
              className="flex-1 min-w-0 flex items-baseline gap-3 rounded-sm hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors cursor-pointer text-left"
            >
              <span className="flex-1 min-w-0 truncate flex items-center gap-1.5">
                Operating cost
                <ChevronRight className="h-3 w-3 text-[#8A9C93] shrink-0" strokeWidth={2} />
              </span>
              <span className="w-14 pr-2 shrink-0" aria-hidden="true" />
              <span className="font-mono-data text-[#B65A45] tabular-nums whitespace-nowrap w-28 text-right shrink-0">
                −{fmt(cost)}
              </span>
            </button>
          ) : (
            <>
              <span className="flex-1 min-w-0 truncate">Operating cost</span>
              <span className="w-14 pr-2 shrink-0" aria-hidden="true" />
              <span className="font-mono-data text-[#B65A45] tabular-nums whitespace-nowrap w-28 text-right shrink-0">
                −{fmt(cost)}
              </span>
            </>
          )}
        </div>
        {/* Inline per-category breakdown for Operating cost — appears
            directly under the Operating cost row when expanded. Tapping
            the Operating cost row again will collapse it. */}
        {costExpanded && Array.isArray(costBreakdown) && costBreakdown.length ? (
          <div
            data-testid={`${testid}-op-cost-breakdown`}
            className="pl-5 space-y-0.5"
          >
            {costBreakdown.map((line) => (
              <div
                key={line.label}
                data-testid={`${testid}-op-cost-breakdown-${line.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-baseline gap-3 text-[12px] text-[#5C746A]"
              >
                <span className="flex-1 min-w-0 truncate">{line.label}</span>
                <span className="w-14 pr-2 shrink-0" aria-hidden="true" />
                <span className="font-mono-data text-[#8A9C93] tabular-nums whitespace-nowrap w-28 text-right shrink-0">
                  −{fmt(line.amount)}
                </span>
              </div>
            ))}
            {onCostClick ? (
              <button
                type="button"
                onClick={onCostClick}
                data-testid={costTestid}
                className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-semibold text-[#2B5E4A] hover:text-[#1B3B2E] transition-colors"
              >
                View per-patient audit
                <ChevronRight className="h-3 w-3 shrink-0" strokeWidth={2} />
              </button>
            ) : null}
          </div>
        ) : null}
        {extraCostLines.map((c) => {
          const inner = (
            <>
              <span className="flex-1 min-w-0 truncate flex items-center gap-1.5">
                {c.label}
                {c.onClick ? (
                  <ChevronRight className="h-3 w-3 text-[#8A9C93] shrink-0" strokeWidth={2} />
                ) : null}
              </span>
              <span className="w-14 pr-2 shrink-0" aria-hidden="true" />
              <span className="font-mono-data text-[#B65A45] tabular-nums whitespace-nowrap w-28 text-right shrink-0">
                −{fmt(c.amount || 0)}
              </span>
            </>
          );
          if (c.onClick) {
            return (
              <button
                type="button"
                key={c.label}
                onClick={c.onClick}
                data-testid={c.testid}
                className="w-full text-left flex items-baseline gap-3 rounded-sm hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors cursor-pointer"
              >
                {inner}
              </button>
            );
          }
          return (
            <div key={c.label} className="flex items-baseline gap-3">
              {inner}
            </div>
          );
        })}
        {(() => {
          const extras = extraCostLines.reduce((s, c) => s + (c.amount || 0), 0);
          const totalCost = (cost || 0) + extras;
          if (totalCost <= 0.005) return null;
          const pct =
            totalRevenue > 0
              ? +((totalCost / totalRevenue) * 100).toFixed(1)
              : 0;
          return (
            <div
              data-testid={`${testid}-total-cost`}
              className="flex items-baseline gap-3 pt-1 mt-1 border-t border-[#F0F4F2]"
            >
              <span className="flex-1 min-w-0 font-semibold text-[#1B3B2E] truncate">
                Total costs
              </span>
              <span
                data-testid={`${testid}-total-cost-pct`}
                className="font-mono-data text-[#8A9C93] tabular-nums whitespace-nowrap w-14 text-right pr-2 shrink-0"
              >
                {pct.toFixed(1)}%
              </span>
              <span
                data-testid={`${testid}-total-cost-amount`}
                className="font-mono-data font-semibold text-[#B65A45] tabular-nums whitespace-nowrap w-28 text-right shrink-0"
              >
                −{fmt(totalCost)}
              </span>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// Inline editor for the monthly operating cost. Defaults are scope-aware
// ($20k Non-HCV, $0 HCV); the pencil icon flips into an input so the user
// can type a new monthly figure. Persists via the parent's onSave.
export function OperatingCostEditor({
  value,
  onSave,
  editing,
  setEditing,
  scopeLabel,
  perPatient = false,
  patientCount = 0,
  effectiveTotal = null,
}) {
  const [draft, setDraft] = useState(String(value ?? 0));
  useEffect(() => {
    if (!editing) setDraft(String(value ?? 0));
  }, [value, editing]);
  const commit = () => {
    const cleaned = String(draft).replace(/[^0-9.]/g, "");
    onSave(cleaned === "" ? 0 : Number(cleaned));
    setEditing(false);
  };
  const cancel = () => {
    setDraft(String(value ?? 0));
    setEditing(false);
  };
  const unitLabel = perPatient
    ? `${scopeLabel} op. cost / patient`
    : `${scopeLabel} op. cost / mo`;
  return (
    <div
      data-testid="op-cost-editor"
      className="inline-flex flex-wrap items-center gap-2 rounded-lg border border-[#E6EBE8] bg-[#F7F9F8] px-2.5 py-1.5 text-[11px] text-[#5C746A]"
    >
      <span className="font-bold uppercase tracking-[0.12em]">
        {unitLabel}
      </span>
      {editing ? (
        <>
          <input
            data-testid="op-cost-input"
            type="text"
            inputMode="decimal"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") cancel();
            }}
            className="font-mono-data tabular-nums w-24 px-2 py-1 rounded-md border border-[#E6EBE8] bg-white text-[#1B3B2E] text-right focus:outline-none focus:ring-2 focus:ring-[#2B5E4A]/30"
          />
          <button
            type="button"
            data-testid="op-cost-save"
            onClick={commit}
            className="px-2 py-1 rounded-md bg-[#1B3B2E] text-white text-[11px] font-medium hover:bg-[#2B5E4A] transition-colors"
          >
            Save
          </button>
          <button
            type="button"
            data-testid="op-cost-cancel"
            onClick={cancel}
            className="px-2 py-1 rounded-md text-[#5C746A] text-[11px] hover:text-[#1B3B2E] transition-colors"
          >
            Cancel
          </button>
        </>
      ) : (
        <>
          <span
            data-testid="op-cost-value"
            className="font-mono-data tabular-nums font-semibold text-[#1B3B2E]"
          >
            {formatCurrency(value || 0)}
          </span>
          {perPatient ? (
            <span
              data-testid="op-cost-multiplier"
              className="font-mono-data tabular-nums text-[10px] text-[#5C746A]"
            >
              × {patientCount} pt = {formatCurrency(effectiveTotal ?? 0)}
            </span>
          ) : null}
          <button
            type="button"
            data-testid="op-cost-edit"
            onClick={() => setEditing(true)}
            aria-label="Edit operating cost"
            title="Edit operating cost"
            className="p-1 rounded-md text-[#5C746A] hover:text-[#1B3B2E] hover:bg-white transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </>
      )}
    </div>
  );
}
